/**
 * Custom hook for marketplace state management
 * Extracts complex state logic from CustomMarketplacePage
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import type {
  CustomMarketplace,
  CustomMarketplaceProductData,
  FulfillmentOption,
  CategoryColumnDefaults,
} from '../../types/database';
import type { Product } from '../../types';
import {
  getAllCustomMarketplaces,
  getProductDataByMarketplace,
  getAllProducts,
  getCategoryDefaultsByMarketplace,
} from '../../utils/database';
import { fetchExchangeRates } from '../../utils/currency';
import { logger } from '../../utils/logger';
import type { ViewMode, SettingsTab } from './types';
import { AMAZON_US_FULFILLMENT, AMAZON_DEFAULT_FULFILLMENT } from './types';

interface UseMarketplaceOptions {
  initialMarketplaceId?: string;
}

export function useMarketplace(options: UseMarketplaceOptions = {}) {
  const { initialMarketplaceId } = options;

  // Core state
  const [marketplaces, setMarketplaces] = useState<CustomMarketplace[]>([]);
  const [selectedMarketplace, setSelectedMarketplace] = useState<CustomMarketplace | null>(null);
  const [selectedCountry, setSelectedCountry] = useState<string>('');
  const [selectedFulfillment, setSelectedFulfillment] = useState<string>('');
  const [products, setProducts] = useState<Product[]>([]);
  const [productData, setProductData] = useState<CustomMarketplaceProductData[]>([]);

  // UI state
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [settingsTab, setSettingsTab] = useState<SettingsTab>('general');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Currency state
  const [exchangeRates, setExchangeRates] = useState<Record<string, number>>({});
  const [isLoadingRates, setIsLoadingRates] = useState(false);

  // Category defaults
  const [categoryDefaults, setCategoryDefaults] = useState<CategoryColumnDefaults[]>([]);

  // Computed values
  const selectedCountryData = useMemo(() => {
    return selectedMarketplace?.countries.find(c => c.code === selectedCountry);
  }, [selectedMarketplace, selectedCountry]);

  const fulfillmentOptions = useMemo((): FulfillmentOption[] => {
    if (!selectedCountryData) return [];

    // Use country-specific options if available
    if (selectedCountryData.fulfillmentOptions?.length) {
      return selectedCountryData.fulfillmentOptions;
    }

    // Fall back to marketplace options
    if (selectedMarketplace?.shortName === 'AMZ') {
      return selectedCountry === 'US' ? AMAZON_US_FULFILLMENT : AMAZON_DEFAULT_FULFILLMENT;
    }

    return [];
  }, [selectedMarketplace, selectedCountry, selectedCountryData]);

  const visibleColumns = useMemo(() => {
    return selectedMarketplace?.columns.filter(col => col.visible) || [];
  }, [selectedMarketplace]);

  // Load initial data
  useEffect(() => {
    const loadData = async () => {
      try {
        const [allMarketplaces, allProducts] = await Promise.all([
          getAllCustomMarketplaces(),
          getAllProducts(),
        ]);
        setMarketplaces(allMarketplaces);
        setProducts(allProducts);

        // Load exchange rates
        setIsLoadingRates(true);
        const rates = await fetchExchangeRates();
        setExchangeRates(rates.rates);
        setIsLoadingRates(false);

        // Select initial marketplace if provided
        if (initialMarketplaceId) {
          const initial = allMarketplaces.find(m => m.id === initialMarketplaceId);
          if (initial) {
            setSelectedMarketplace(initial);
            if (initial.countries.length > 0) {
              const country = initial.countries.find(c => c.isActive) || initial.countries[0];
              setSelectedCountry(country.code);
              // Set default fulfillment
              const fulfillmentOpts = country.fulfillmentOptions || [];
              if (fulfillmentOpts.length > 0) {
                setSelectedFulfillment(fulfillmentOpts.find(f => f.isDefault)?.code || fulfillmentOpts[0].code);
              }
            }
            setViewMode('table');
          }
        }
      } catch (error) {
        logger.error('Failed to load data:', error);
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, [initialMarketplaceId]);

  // Load product data when marketplace or country changes
  useEffect(() => {
    const loadProductData = async () => {
      if (!selectedMarketplace || !selectedCountry) {
        setProductData([]);
        return;
      }

      try {
        const data = await getProductDataByMarketplace(selectedMarketplace.id, selectedCountry);
        setProductData(data);
      } catch (error) {
        logger.error('Failed to load product data:', error);
      }
    };
    loadProductData();
  }, [selectedMarketplace, selectedCountry]);

  // Load category defaults when marketplace changes
  useEffect(() => {
    const loadCategoryDefaults = async () => {
      if (!selectedMarketplace) {
        setCategoryDefaults([]);
        return;
      }

      try {
        const defaults = await getCategoryDefaultsByMarketplace(selectedMarketplace.id);
        setCategoryDefaults(defaults);
      } catch (error) {
        logger.error('Failed to load category defaults:', error);
      }
    };
    loadCategoryDefaults();
  }, [selectedMarketplace]);

  // Handlers
  const handleSelectMarketplace = useCallback((mp: CustomMarketplace) => {
    setSelectedMarketplace(mp);
    setViewMode('table');

    // Set first active country
    const activeCountry = mp.countries.find(c => c.isActive) || mp.countries[0];
    if (activeCountry) {
      setSelectedCountry(activeCountry.code);

      // Set default fulfillment
      const fulfillmentOpts = activeCountry.fulfillmentOptions ||
        (mp.shortName === 'AMZ' && activeCountry.code === 'US' ? AMAZON_US_FULFILLMENT : AMAZON_DEFAULT_FULFILLMENT);

      if (fulfillmentOpts.length > 0) {
        const defaultFulfillment = fulfillmentOpts.find(f => f.isDefault) || fulfillmentOpts[0];
        setSelectedFulfillment(defaultFulfillment.code);
      }
    }
  }, []);

  const handleBackToGrid = useCallback(() => {
    setViewMode('grid');
    setSelectedMarketplace(null);
    setSelectedCountry('');
    setSelectedFulfillment('');
  }, []);

  const refreshRates = useCallback(async () => {
    setIsLoadingRates(true);
    try {
      const rates = await fetchExchangeRates();
      setExchangeRates(rates.rates);
    } catch (error) {
      logger.error('Failed to refresh rates:', error);
    } finally {
      setIsLoadingRates(false);
    }
  }, []);

  const reloadProducts = useCallback(async () => {
    const allProducts = await getAllProducts();
    setProducts(allProducts);
  }, []);

  const reloadCategoryDefaults = useCallback(async () => {
    if (!selectedMarketplace) return;
    const defaults = await getCategoryDefaultsByMarketplace(selectedMarketplace.id);
    setCategoryDefaults(defaults);
  }, [selectedMarketplace]);

  return {
    // State
    marketplaces,
    setMarketplaces,
    selectedMarketplace,
    setSelectedMarketplace,
    selectedCountry,
    setSelectedCountry,
    selectedFulfillment,
    setSelectedFulfillment,
    products,
    setProducts,
    productData,
    setProductData,
    viewMode,
    setViewMode,
    settingsTab,
    setSettingsTab,
    isLoading,
    isSaving,
    setIsSaving,
    exchangeRates,
    isLoadingRates,
    categoryDefaults,
    setCategoryDefaults,

    // Computed
    selectedCountryData,
    fulfillmentOptions,
    visibleColumns,

    // Handlers
    handleSelectMarketplace,
    handleBackToGrid,
    refreshRates,
    reloadProducts,
    reloadCategoryDefaults,
  };
}
